import { useTranslation } from 'react-i18next'
import PurposeSummaryCell from './PurposeSummaryCell'

/**
 * VH-81: Renders one timing group section (Morning / Morning & Evening / Evening / Unscheduled).
 *
 * - Hidden entirely when `rows` is empty (no empty header shown).
 * - Header shows translated group name + count.
 * - Table: Name | Dose | Notes (purpose summary).
 * - Print-friendly: section avoids mid-section page breaks via `break-inside: avoid`.
 */
export default function TimingGroupSection({
  groupKey,
  rows,
  language,
  translateUnit,
  translateForm,
}) {
  const { t } = useTranslation()

  if (!rows || rows.length === 0) {
    return null
  }

  // Compose the section header: e.g. "🌅 Morning (3)"
  const emojiByGroup = {
    fluid: '💧',
    morning: '🌅',
    morning_evening: '🌅🌙',
    evening: '🌙',
    unscheduled: '🕓',
  }
  const emoji = emojiByGroup[groupKey] || ''
  const groupName = t(`report.groups.${groupKey}`)
  const headerText = t('report.groupCount', { name: groupName, count: rows.length })

  // Formats the dose column. For morning_evening, show both; else show whichever is nonzero.
  const formatDose = (row) => {
    const parts = []
    if (row.morning > 0) {
      parts.push(`${row.morning} ${translateUnit(row.unit)} ${t('report.morning').toLowerCase()}`)
    }
    if (row.midday > 0) {
      parts.push(`${row.midday} ${translateUnit(row.unit)} ${t('report.midday').toLowerCase()}`)
    }
    if (row.evening > 0) {
      parts.push(`${row.evening} ${translateUnit(row.unit)} ${t('report.evening').toLowerCase()}`)
    }
    return parts.length > 0 ? parts.join(', ') : '-'
  }

  return (
    <section className="mb-6 print:mb-2 break-inside-avoid" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
      <h2 className="text-lg font-bold text-gray-800 mb-2 print:text-xs print:mb-0.5">
        <span className="print:hidden mr-1">{emoji}</span>
        {headerText}
      </h2>
      <div className="bg-surface-container rounded-lg shadow-md overflow-hidden border border-gray-200 print:rounded-none print:shadow-none">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 print:px-2 print:py-1 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-1/4">
                {t('report.name')}
              </th>
              <th className="px-6 py-3 print:px-2 print:py-1 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-1/4">
                {t('report.summaryDose')}
              </th>
              <th className="px-6 py-3 print:px-2 print:py-1 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                {t('report.notes')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-container divide-y divide-gray-200">
            {rows.map((row, idx) => (
              <tr key={`${groupKey}-${row.supplementId}-${idx}`} className={idx % 2 === 0 ? 'bg-surface-container' : 'bg-gray-50'}>
                <td className="px-6 py-4 print:px-2 print:py-0.5 border-r border-gray-200 align-top">
                  <div className="font-semibold text-gray-900 truncate max-w-[260px]" title={row.name}>
                    {row.name}
                  </div>
                  {row.brand && (
                    <div className="text-sm text-gray-600 print:hidden">{row.brand}</div>
                  )}
                  <div className="text-xs text-gray-500 print:hidden">{translateForm(row.form)}</div>
                </td>
                <td className="px-6 py-4 print:px-2 print:py-0.5 text-sm text-gray-900 border-r border-gray-200 align-top">
                  {formatDose(row)}
                </td>
                <td className="px-6 py-4 print:px-2 print:py-0.5 align-top">
                  <PurposeSummaryCell
                    supplementId={row.supplementId}
                    initialSummary={row.purposeSummary}
                    language={language}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
